import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import AdminLayout from "../../../layouts/AdminLayout";
import DataTableLayout from "../../../layouts/DataTableLayout";
import DataTable from "../../../components/common/DataTable";
import Button from "../../../components/ui/Button";
import { Plus } from "lucide-react";
import FilterBar from "../../../components/common/FilterBar";
import { getMySupportTickets } from "../../../services/supportTicketService";
import Skeleton from "../../../components/ui/Skeleton";
import StatusBadge from "../../../components/ui/StatusBadge";

const ResidentSupportTickets = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [tickets, setTickets] = useState([]);

  const [filters, setFilters] = useState({ search: "" });
  const [appliedFilters, setAppliedFilters] = useState({ search: "" });

  const fetchTickets = async () => {
    setLoading(true);
    try {
      const data = await getMySupportTickets();
      setTickets(data?.tickets || []);
    } catch (err) {
      toast.error(err?.message || "Unable to load support tickets.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTickets();
  }, []);

  const filtered = useMemo(() => {
    const search = String(appliedFilters.search || "").trim().toLowerCase();
    if (!search) return tickets;
    return tickets.filter((t) => {
      return (
        String(t.subject || "").toLowerCase().includes(search) ||
        String(t.description || "").toLowerCase().includes(search) ||
        String(t.status || "").toLowerCase().includes(search)
      );
    });
  }, [tickets, appliedFilters.search]);

  const getStatusBadge = (status) => {
    const s = String(status || "").toLowerCase();
    if (s === "closed") {
      return <StatusBadge status="approved" label="Closed" />;
    }
    if (s === "in_progress") {
      return <StatusBadge status="pending" label="In Progress" />;
    }
    return <StatusBadge status="warning" label="Open" />;
  };

  const columns = [
    {
      header: "Ticket #",
      render: (row) => (
        <span style={{ fontFamily: "monospace", fontWeight: "700", color: "#334155" }}>
          #{row.ticket_id}
        </span>
      ),
    },
    {
      header: "Subject",
      render: (row) => (
        <span style={{ fontWeight: "600", color: "#1e293b" }}>{row.subject || "-"}</span>
      ),
    },
    { header: "Status", render: (row) => getStatusBadge(row.status) },
    {
      header: "Created",
      render: (row) => (
        <span style={{ fontSize: "12.5px", color: "#64748b" }}>
          {row.created_at ? new Date(row.created_at).toLocaleString() : "-"}
        </span>
      ),
    },
  ];

  return (
    <AdminLayout>
      <DataTableLayout
        title="My Support Tickets"
        filters={
          <FilterBar
            filtersConfig={[
              { key: "search", label: "Search", type: "text", placeholder: "Search tickets..." },
            ]}
            filters={filters}
            setFilters={setFilters}
            onApply={() => setAppliedFilters(filters)}
            onClear={() => {
              const empty = { search: "" };
              setFilters(empty);
              setAppliedFilters(empty);
            }}
          />
        }
        actions={
          <div className="flex gap-2">
            <Button variant="outline" onClick={fetchTickets}>
              Refresh
            </Button>
            <Button icon={<Plus size={15} />} onClick={() => navigate("/resident/support-tickets/new")}>Create Ticket</Button>
          </div>
        }
      >
        {loading ? (
          <Skeleton variant="list" count={3} />
        ) : (
          <DataTable columns={columns} data={filtered} />
        )}
      </DataTableLayout>
    </AdminLayout>
  );
};

export default ResidentSupportTickets;

